import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useShallow } from 'zustand/react/shallow';
import { useWizardStore } from '../wizard-store';

/**
 * StepNavBar — M1.1 lineáris léptetés-navigáció (ADR-103 Am-4, I-1..I-4).
 *
 * In-flow sáv a step-szegmens alján (NEM a rail-ben — a rail tiszta ár +
 * step4-Kiadás marad). A store `goNext/goPrev` action-jeit hívja, mind SOFT.
 *
 * Invariáns (Winston-keret):
 *  - Tovább = SOFT: SOHA nem gate-el, bármikor előre (ugyanaz mint a
 *    breadcrumb-jump). Ha az aktuális step invalid (`!stepDone`), egy statikus
 *    hint jelez, de a gomb AKTÍV marad — NEM per-step blokk.
 *  - HARD-aggregate-gate KIZÁRÓLAG a step4-véglegesítésnél (`isSubmittable`,
 *    a rail StickySummaryFooter „Kiadás" gombja) → itt NINCS Tovább a step4-en.
 *  - step1 Vissza disabled (nincs hova) — látható, hogy a layout ne ugráljon.
 */
export function StepNavBar() {
  const { t } = useTranslation();
  const { activeStep, goNext, goPrev, stepDone } = useWizardStore(
    useShallow(s => ({
      activeStep: s.activeStep,
      goNext: s.goNext,
      goPrev: s.goPrev,
      stepDone: s.stepDone,
    }))
  );

  const isFirst = activeStep === 1;
  const isLast = activeStep === 4;
  // Statikus hint (Zeno #3): NEM echo-zza a beírt értéket → nem SR-spam
  // billentyűnként; csak az invalid-állapotot jelzi egyszer.
  const showHint = !stepDone(activeStep);

  return (
    <nav
      aria-label={t('rental.stepNav.navLabel')}
      className="mt-6 flex items-center justify-between gap-3 border-t border-[var(--border-secondary,#e2e8f0)] pt-4"
    >
      <Button
        type="button"
        variant="outline"
        onClick={goPrev}
        disabled={isFirst}
        aria-disabled={isFirst}
      >
        <span aria-hidden="true">←</span> {t('rental.stepNav.prev')}
      </Button>

      <span className="text-xs font-medium text-[var(--text-muted-foreground,#64748b)]">
        {t('rental.stepNav.stepCounter', { current: activeStep, total: 4 })}
      </span>

      <div className="flex items-center gap-3">
        {/* SOFT-hint: aria-live=polite, statikus szöveg (nem per-keystroke). */}
        {showHint && !isLast && (
          <span aria-live="polite" className="text-xs text-[var(--text-muted-foreground,#64748b)]">
            {t('rental.stepNav.incompleteHint')}
          </span>
        )}
        {/* step4-en NINCS Tovább — a HARD-finalize a rail-Kiadás (isSubmittable). */}
        {!isLast && (
          <Button type="button" onClick={goNext}>
            {t('rental.stepNav.next')}: {t(`rental.stepWizard.step${activeStep + 1}`)}{' '}
            <span aria-hidden="true">→</span>
          </Button>
        )}
      </div>
    </nav>
  );
}
